<template>
  <div class="space-y-4" data-testid="runner-watchdog">
    <RunnerToggle
      id="watchdogEnabled"
      :model-value="enabled"
      label="Watchdog"
      :description="WATCHDOG_DESCRIPTION"
      :disabled="!available"
      @update:model-value="emit('update:enabled', $event)"
    />
    <p v-if="!available" id="watchdog-unavailable" class="text-sm text-gray-500 dark:text-gray-400">
      Needs "Show in dashboard" or a Metrics destination.
    </p>
    <p
      v-else-if="note && !enabled"
      id="watchdog-note"
      class="text-sm text-amber-700 dark:text-amber-400"
    >
      {{ note }}
    </p>
    <div>
      <label
        for="watchdogTimeoutMinutes"
        class="block text-sm/6 font-medium text-gray-900 dark:text-white"
        >Restart after (minutes without a report)</label
      >
      <div class="mt-2 max-w-40">
        <input
          id="watchdogTimeoutMinutes"
          name="watchdogTimeoutMinutes"
          type="number"
          :min="WATCHDOG_MIN_TIMEOUT"
          :max="WATCHDOG_MAX_TIMEOUT"
          step="1"
          :value="timeoutMinutes"
          :disabled="!enabled"
          :aria-invalid="!!shownError"
          :aria-describedby="shownError ? 'watchdog-error' : undefined"
          class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:focus:outline-indigo-500 dark:disabled:bg-white/10"
          :class="
            shownError
              ? 'outline-red-300 dark:outline-red-500/50'
              : 'outline-gray-300 dark:outline-white/10'
          "
          @input="onInput"
        />
      </div>
    </div>
    <p v-if="shownError" id="watchdog-error" class="text-sm text-red-600 dark:text-red-400">
      {{ shownError }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import RunnerToggle from '@/components/RunnerToggle.vue';
import {
  validateWatchdogTimeout,
  WATCHDOG_DESCRIPTION,
  WATCHDOG_MAX_TIMEOUT,
  WATCHDOG_MIN_TIMEOUT,
} from '@/lib/watchdog';

const props = defineProps<{
  enabled: boolean;
  timeoutMinutes: number | string;
  /** Whether Monitoring or Metrics is on in the form. */
  available: boolean;
  /** The backend's refusal of the watchdog settings. */
  error?: string;
  /** Shown while the watchdog is off, for example why the platform switched it off. */
  note?: string;
}>();

const emit = defineEmits<{
  (e: 'update:enabled', value: boolean): void;
  (e: 'update:timeoutMinutes', value: number | string): void;
}>();

const shownError = computed(
  () => props.error || (props.enabled ? validateWatchdogTimeout(props.timeoutMinutes) : '')
);

function onInput(event: Event) {
  const raw = (event.target as HTMLInputElement).value;
  emit('update:timeoutMinutes', raw === '' ? '' : Number(raw));
}
</script>
